<template>
  <div>
    <div style="margin: 0 5px; padding-bottom: 60px; padding-top: 10px">
      <!-- 图例开关 -->
      <div style="display: flex; justify-content: flex-end; padding: 5px 10px 5px;">
        <div style="display: flex; align-items: center; color: #aa341b; font-size: 13px; cursor: pointer;" @click="showLegend = !showLegend">
          <van-icon :name="showLegend ? 'close' : 'question-o'" size="16" style="margin-right: 4px;" />
          <span>{{ showLegend ? $t('timeline_hide_legend') : $t('timeline_show_legend') }}</span>
        </div>
      </div>

      <!-- 图例内容 -->
      <div v-show="showLegend" class="legend-container">
        <div class="legend-group">
          <div class="legend-group-title">{{ $t('timeline_legend_internal') }}</div>
          <div class="legend-items">
            <span v-for="(color, label) in internalLegendMap" :key="label" class="legend-item">
              <span class="legend-dot" :style="{backgroundColor: color}"></span>
              <span class="legend-text">{{ label }}</span>
            </span>
          </div>
        </div>
        <div class="legend-group">
          <div class="legend-group-title">{{ $t('timeline_legend_online') }}</div>
          <div class="legend-items">
            <span v-for="(color, label) in onlineLegendMap" :key="label" class="legend-item">
              <span class="legend-dot" :style="{backgroundColor: color}"></span>
              <span class="legend-text">{{ label }}</span>
            </span>
          </div>
        </div>
        <div class="legend-group">
          <div class="legend-group-title">{{ $t('timeline_legend_external') }}</div>
          <div class="legend-items">
            <span v-for="(color, label) in externalLegendMap" :key="label" class="legend-item">
              <span class="legend-dot" :style="{backgroundColor: color}"></span>
              <span class="legend-text">{{ label }}</span>
            </span>
          </div>
        </div>
        <div class="legend-group">
          <div class="legend-group-title">{{ $t('timeline_legend_academic') }}</div>
          <div class="legend-items">
            <span v-for="(color, label) in academicLegendMap" :key="label" class="legend-item">
              <span class="legend-dot" :style="{backgroundColor: color}"></span>
              <span class="legend-text">{{ label }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- timeline主结构 -->
      <AsyncList ref="timelineAsyncListRef" :fn="fetchTimelineData">
        <template v-slot="{list}">
          <van-steps direction="vertical" :active="-1">
            <van-step
              v-for="(monthGroup, idx) in partitionTimelineByMonth(list)"
              :key="monthGroup.month"
            >
              <div style="text-align: left; color: #333; display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px;">
                <div class="timeline-month-row" @click="toggleMonthExpand(monthGroup.month, idx === 0)">
                  <span style="font-weight: bold; font-size: 18px;" v-html="formatMonthLabel(monthGroup.month)"></span>
                  <span class="timeline-summary-action" style="font-size: 13px; flex-shrink: 0;">
                    <template v-if="!expandedMonths[monthGroup.month] && !(idx === 0 && expandedMonths[monthGroup.month] === undefined)">
                      <span style="color:#aa341b; margin-left:8px; display: inline-flex; align-items: center;">{{ $t('timeline_expand') }} <van-icon name="arrow-down" style="margin-left: 2px" /></span>
                    </template>
                    <template v-else>
                      <span style="color:#666; display: inline-flex; align-items: center;">{{ $t('timeline_collapse') }} <van-icon name="arrow-up" style="margin-left: 2px" /></span>
                    </template>
                  </span>
                </div>
                <!-- 详情收起时折行显示summary -->
                <div class="timeline-summary-text" @click="toggleMonthExpand(monthGroup.month, idx === 0)" v-html="renderMonthSummary(monthGroup.items)">
                </div>
              </div>
              <ul v-show="idx === 0 ? expandedMonths[monthGroup.month] !== false : expandedMonths[monthGroup.month]" class="timeline-card-group">
                <li v-for="(item, index) in monthGroup.items" :key="index" class="timeline-card">
                  <div class="card-header">
                    <span style="display: flex; align-items: center;">
                      <span :style="{backgroundColor: labelColorMap[item.typeKey] || '#ccc', width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block', marginRight: '6px'}"></span>
                      <span style="color: #333; font-size: 14px; font-weight: bold;">
                        {{ item.typeLabel }}
                      </span>
                    </span>
                    <span style="font-size: 12px; color: #999; margin-left: auto;">{{ item.date ? item.date.split('T')[0] : '' }}</span>
                  </div>
                  <div v-if="item.typeKey !== 'msl_call' && item.typeKey !== 'msl_insight' && item.title !== item.typeLabel" class="card-title">{{ item.title }}</div>
                  <div v-if="isInternalTimelineType(item.typeKey)" style="margin-bottom: 2px; display: flex; justify-content: space-between; align-items: flex-start;">
                    <div v-if="item.products || item.product" style="flex: 1;">
                      <van-tag v-for="product in (item.products || item.product).split(' ').filter(Boolean)" :key="product" color="#f5f5f5" text-color="#666" style="margin-right: 4px; margin-bottom: 2px; font-size: 12px; padding: 2px 4px;">{{ product }}</van-tag>
                    </div>
                    <div v-else style="flex: 1;"></div>
                    <div v-if="item.user_name || item.insight_user_name" style="font-size: 12px; color: #666; display: flex; align-items: center; white-space: nowrap; margin-left: 10px;">
                      <van-icon name="user-o" style="margin-right: 4px" /> {{ item.user_name || item.insight_user_name }}
                    </div>
                  </div>
                  <div v-if="item.platform || item.meeting" class="info-details">
                    <span>
                      <span v-if="item.platform || item.meeting">
                        {{ item.platform || item.meeting }}
                      </span>
                    </span>
                  </div>
                  <Tags v-if="item.keywordsArr && item.keywordsArr.length" :tag-item="item.keywordsArr" style="margin-top: 6px;" />

                  <div v-if="item.insight_content" class="insight-content">
                    <div class="insight-text">
                      <van-icon v-if="item.insight_content" name="notes-o" color="#999" class="insight-icon" />
                      <span v-if="item.insight_content">{{ item.insight_content }}</span>
                    </div>
                  </div>
                  <div v-if="item.url" class="insight-content" style="padding: 4px; padding-right: 0; background: transparent; align-items: flex-end;">
                    <div class="link-detail" @click="goUrl(item.url)">{{ $t('timeline_detail') }} <van-icon name="arrow" /></div>
                  </div>
                </li>
              </ul>
            </van-step>
          </van-steps>
        </template>
      </AsyncList>
    </div>
  </div>
</template>

<script>
import CommonHeader from '@/components/CommonHeader';
import {Tabs, Tab, Loading, Step, Steps, Tag, Icon} from 'vant';
import physicianMeetingEx from '@/views/hcp/components/physicianMeetingEx.vue';
import * as moment from 'moment';
import Nodata from '@/components/Nodata';
import * as py from '@/api/Physicians';
import {fetchPhysicianEngagements, fetchPhysicianMslInsights} from '@/api/kol';
import {formatterDatenEn} from '@/filters/index';
import AsyncList from '@/components/AsyncList.vue';
import Tags from '@/components/Tags';
import asyncData from '@/mixins/asyncData';
import {getLocalStorage} from '@/utils';
import {fetchPhysicianDataStats} from '@/api/kol';
import {getAllContentSend, getAllContentOpen} from '@/api/content';
import Vue from 'vue';
import {groupBy} from 'lodash';

Vue.use(Step);
Vue.use(Steps);
Vue.use(Tag);
Vue.use(Icon);

export default {
  components: {
    Tags,
    AsyncList,
  },
  // mixins: [
  //   asyncData(
  //     () => fetchPhysicianDataStats(this.$route.query?.physician_id),
  //     'statistic',
  //   ),
  // ],
  props: {
    showInsight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      detailLoading: false,
      internalDetailLoading: false,
      newsList: [],
      meetingsVisitList: [],
      externalFilterList: [
        {
          title: '公众号',
          value: '公众号',
          active: true,
          field: 'type',
          // children: [
          //   {
          //     title: '第一作者',
          //     value: [1, 3],
          //     active: false,
          //     field: 'author_seq_no',
          //   },
          //   {
          //     title: '其他',
          //     value: 0,
          //     active: false,
          //     field: 'author_seq_no',
          //   },
          // ],
        },
        {
          title: '第三方会议',
          value: '第三方会议',
          active: true,
          field: 'type',
        },
        {
          title: '患教文章',
          value: '患教文章',
          active: true,
          field: 'type',
        },
        {
          title: '患教直播',
          value: '患教直播',
          active: true,
          field: 'type',
        },
        {
          title: '学术成果',
          value: 'academic',
          active: true,
          field: 'type',
        }
      ],
      internalFilterList: [
        {
          title: '销售拜访',
          value: 'mr拜访',
          active: true,
          field: 'type',
          // children: [
          //   {
          //     title: '第一作者',
          //     value: [1, 3],
          //     active: false,
          //     field: 'author_seq_no',
          //   },
          //   {
          //     title: '其他',
          //     value: 0,
          //     active: false,
          //     field: 'author_seq_no',
          //   },
          // ],
        },
        {
          title: '医学拜访',
          value: 'msl拜访',
          active: true,
          field: 'type',
        },
        {
          title: '内部会议',
          value: '会议',
          active: true,
          field: 'type',
        },
      ],
      filterType: 'all', // all/internal/external
      expandedMonths: {}, // { '2026-03': true }
      hasMoreExternal: true,
      hasMoreInternal: true,
      hasMoreInsight: true,
      active: 0,
      labels: ['最近动态', '近一年动态', '最近参会', '近一年参会'],
      internalLabels: [
        '近三月销售拜访',
        '近三月医学拜访',
        '近三月内部会议',
        '最近内部互动',
      ],
      values: null,
      internalValues: null,
      externalDataLastUpdate: '',
      typeCountMap: {}, // { '销售拜访': 25, '医学拜访': 10, ... }      // values: ['3月前', '8', '1年前', '4', '4天前', '32', '3天前', '7天前'],
      showLegend: false,
    };
  },
  computed: {
    physician_id() {
      return this.$route.query?.physician_id;
    },
    labelColorMap() {
      return {
        mr_call: '#0561B9',
        msl_call: '#06B0F0',
        internal_meeting: '#FFBF00',
        msl_insight: '#00BCD4',
        content_send: '#ff845f',
        content_open: '#17a772',
        wechat_article: '#3DAA45',
        patient_article: '#13AEF1',
        patient_live: '#0561B9',
        third_party_meeting: '#FFBF00',
        publication: '#9C27B0',
        clinical_trial: '#E91E63',
        national_fund: '#3F51B5',
        award: '#FF9800',
        guideline: '#4CAF50',
        patent: '#00BCD4',
      };
    },
    internalLegendMap() {
      return this.buildLegendMap(['mr_call', 'msl_call', 'internal_meeting', 'msl_insight']);
    },
    onlineLegendMap() {
      return this.buildLegendMap(['content_send', 'content_open']);
    },
    externalLegendMap() {
      return this.buildLegendMap(['wechat_article', 'patient_article', 'patient_live', 'third_party_meeting']);
    },
    academicLegendMap() {
      return this.buildLegendMap(['publication', 'clinical_trial', 'national_fund', 'award', 'guideline', 'patent']);
    },
    internalFilterParams() {
      const filterValueList = this.internalFilterList
        .filter((item) => item.active)
        .map((item) => item.value);
      return {engagement_type: filterValueList};
    },
  },
  watch: {
    internalFilterList: {
      handler() {
        this.$refs.timelineAsyncListRef?.run?.();
      },
      deep: true,
    },
    externalFilterList: {
      handler() {
        this.$refs.timelineAsyncListRef?.run?.();
      },
      deep: true,
    },
  },
  mounted() {
    fetchPhysicianDataStats(this.physician_id).then((res) => {
      this.statistic = res;
      const {
        activity_last_update_text,
        activity_last_year_no,
        meeting_ex_last_update_text,
        meeting_ex_last_year_no,
      } = this.statistic || {};
      this.values = [
        activity_last_update_text,
        activity_last_year_no,
        meeting_ex_last_update_text,
        meeting_ex_last_year_no,
      ];
      const {
        call_last_3m_no,
        mslcall_last_3m_no,
        meeting_last_3m_no,
        engagement_last_update_text,
      } = this.statistic || {};
      this.internalValues = [
        call_last_3m_no,
        mslcall_last_3m_no,
        meeting_last_3m_no,
        engagement_last_update_text,
      ];
      this.externalDataLastUpdate = formatterDatenEn(
        this.statistic.external_data_last_update,
      );
    });
    this.getList();
  },
  methods: {
    formatterDatenEn,
    fetchPhysicianEngagements(pagination) {
      return fetchPhysicianEngagements(
        this.physician_id,
        this.internalFilterParams,
        pagination,
      );
    },
    getList(filter) {
      this.newsList = [];
      this.detailLoading = true;
      py.getVPhysicianActivities(this.physician_id, filter)
        .then((res) => {
          if (res && res.value.length) {
            res.value.forEach((ele) => {
              let labelsArr = [];
              if (ele.labels) {
                labelsArr = ele.labels.split(',');
              }
              this.newsList.push({
                platform: ele.platform,
                title: ele.title,
                url: ele.url,
                date:
                  ele.activity_date && moment(ele.activity_date).isValid()
                    ? moment(ele.activity_date).format('YYYY-MM-DD')
                    : ele.activity_date,
                data_sort:
                  ele.activity_date_sort &&
                  moment(ele.activity_date_sort).isValid()
                    ? moment(ele.activity_date_sort).format('YYYY-MM-DD')
                    : ele.activity_date_sort,
                isNew:
                  ele.activity_date &&
                  moment(ele.activity_date).isValid() &&
                  moment(ele.activity_date).isAfter(
                    moment().subtract(3, 'months'),
                  ),
                activity_type: ele.activity_type,
                keywordsArr: labelsArr,
              });
            });
          }
          this.detailLoading = false;
        })
        .catch((e) => {
          this.detailLoading = false;
        });
    },

    selectFn(index) {
      if (index || index === 0) {
        this.searchList.forEach((ele, $index) => {
          ele.active = false;
          if (index === $index) {
            this.activeIndex = $index;
            ele.active = true;
            this.activeChildren = ele.children;
          }
        });
        this.$emit(
          'selectedFn',
          this.searchList[this.activeIndex],
          this.selectTime,
        );
      }
    },

    goto(url) {
      if (url) {
        location.href = url;
      }
    },
    partitionNewsList(list) {
      const groupData = groupBy(
        list.map((item) => ({
          ...item,
          formatDate: formatterDatenEn(item.engagement_date),
        })),
        (item) => item.formatDate,
      );
      return Object.values(groupData);
    },
    goUrl(url) {
      window.open(url);
    },
    buildLegendMap(typeKeys) {
      return typeKeys.reduce((acc, typeKey) => {
        acc[this.getTypeLabel(typeKey)] = this.labelColorMap[typeKey];
        return acc;
      }, {});
    },
    getTypeLabel(typeKey) {
      const typeLabelMap = {
        mr_call: this.$t('timeline_type_mr_call'),
        msl_call: this.$t('timeline_type_msl_call'),
        internal_meeting: this.$t('timeline_type_internal_meeting'),
        msl_insight: this.$t('timeline_type_msl_insight'),
        content_send: this.$t('timeline_type_content_send'),
        content_open: this.$t('timeline_type_content_open'),
        wechat_article: this.$t('timeline_type_wechat_article'),
        patient_article: this.$t('timeline_type_patient_article'),
        patient_live: this.$t('timeline_type_patient_live'),
        third_party_meeting: this.$t('timeline_type_third_party_meeting'),
        publication: this.$t('timeline_type_publication'),
        clinical_trial: this.$t('timeline_type_clinical_trial'),
        national_fund: this.$t('timeline_type_national_fund'),
        award: this.$t('timeline_type_award'),
        guideline: this.$t('timeline_type_guideline'),
        patent: this.$t('timeline_type_patent'),
      };
      return typeLabelMap[typeKey] || typeKey;
    },
    getEngagementTypeLabel(engagementType) {
      const map = {
        mr拜访: 'mr_call',
        msl拜访: 'msl_call',
        会议: 'internal_meeting',
      };
      return this.getTypeLabel(map[engagementType] || engagementType);
    },
    getPurposeLabel(purpose) {
      const purposeLabelMap = {
        'Delivering product message': this.$t('timeline_purpose_delivering_product_message'),
        'Information collection': this.$t('timeline_purpose_information_collection'),
        'Meeting invataion / Meeting follow up': this.$t('timeline_purpose_meeting_follow_up'),
        'Meeting invitation / Meeting follow up': this.$t('timeline_purpose_meeting_follow_up'),
        'Reminding': this.$t('timeline_purpose_reminding'),
        NRDL: this.$t('timeline_purpose_nrdl'),
        Others: this.$t('timeline_purpose_others'),
        'MKT Activity': this.$t('timeline_purpose_mkt_activity'),
        Positive: this.$t('timeline_purpose_positive'),
      };
      return purposeLabelMap[purpose] || purpose || '';
    },
    isInternalTimelineType(typeKey) {
      return ['mr_call', 'msl_call', 'msl_insight', 'internal_meeting'].includes(typeKey);
    },
    toggleMonthExpand(month, isFirst = false) {
      if (isFirst && this.expandedMonths[month] === undefined) {
        this.$set(this.expandedMonths, month, false);
      } else {
        this.$set(this.expandedMonths, month, !this.expandedMonths[month]);
      }
    },
    // 合并所有数据，按filterType筛选，返回统一timeline数组
    async fetchTimelineData(pagination) {
      if (!pagination || pagination.page === 0) {
        this.hasMoreExternal = true;
        this.hasMoreInternal = true;
        this.hasMoreInsight = true;
        this.typeCountMap = {}; // 重置计数
      }
      // 获取外部、内部、医学洞见、学术数据
      const isAcademicActive = this.externalFilterList.some(item => item.value === 'academic' && item.active);
      const [external, internal, insights, academic, onlineInteractions] = await Promise.all([
        this.fetchExternalData(pagination),
        this.fetchInternalData(pagination),
        this.fetchInsightData(pagination),
        isAcademicActive ? this.fetchAcademicData(pagination) : Promise.resolve([]),
        this.fetchOnlineInteractionData(pagination),
      ]);
      let all = [];
      if (this.filterType === 'all') {
        all = [...external, ...internal, ...insights, ...academic, ...onlineInteractions];
      } else if (this.filterType === 'internal') {
        all = [...internal, ...insights, ...onlineInteractions];
      } else if (this.filterType === 'external') {
        all = [...external, ...academic];
      }
      // 按时间倒序
      all.sort((a, b) => new Date(b.date) - new Date(a.date));
      return all;
    },
    // 线上互动（内容发送/打开）转timeline格式
    async fetchOnlineInteractionData(pagination) {
      if (pagination && pagination.page > 0) return [];

      const [sendRes, openRes] = await Promise.all([
        getAllContentSend(this.physician_id).catch(() => ({ value: [] })),
        getAllContentOpen(this.physician_id).catch(() => ({ value: [] })),
      ]);

      const sendList = (sendRes?.value || []).map((item) => ({
        type: 'online',
        typeKey: 'content_send',
        typeLabel: this.getTypeLabel('content_send'),
        title: item?.content?.content_name || this.$t('timeline_content_send_title'),
        date: item.send_time,
        platform: this.$t('timeline_platform_sent'),
      }));

      const openList = (openRes?.value || []).map((item) => ({
        type: 'online',
        typeKey: 'content_open',
        typeLabel: this.getTypeLabel('content_open'),
        title: item?.content?.content_name || this.$t('timeline_content_open_title'),
        date: item.open_time,
        platform: `${this.$t('open')}${item.duration ? ` · ${this.$t('timeline_read_duration', { duration: this.formatDuration(item.duration) })}` : ''}`,
      }));

      const allOnline = [...sendList, ...openList].filter(item => item.date);
      this.typeCountMap.content_send = sendList.length;
      this.typeCountMap.content_open = openList.length;
      return allOnline;
    },
    formatDuration(seconds) {
      const totalSeconds = Number(seconds || 0);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const remainSeconds = totalSeconds % 60;

      if (this.$i18n.locale === 'zh') {
        const hourPart = hours > 0 ? `${hours}小时` : '';
        const minutePart = minutes > 0 ? `${minutes}分` : '';
        const secondPart = `${remainSeconds}秒`;
        return `${hourPart}${minutePart}${secondPart}`;
      }

      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (remainSeconds > 0 || parts.length === 0) parts.push(`${remainSeconds}s`);
      return parts.join(' ');
    },
    // 外部数据转timeline格式
    async fetchExternalData(pagination) {
      if (!this.hasMoreExternal) return [];
      const filterValueList = this.externalFilterList.filter((item) => item.active && item.value !== 'academic').map((item) => item.value);
      if (!filterValueList.length) return [];

      const res = await py.getVPhysicianActivities(this.physician_id, {activity_type: filterValueList}, pagination?.page || 0, pagination?.pageSize || 10);
      const list = res?.value || [];
      // 保存每个类型的总数计数（从 @odata.count 获取）
      filterValueList.forEach(typeValue => {
        if (!this.typeCountMap[typeValue]) {
          // 获取这个类型的总数（@odata.count）
          const typeSpecificCount = res?.['@odata.count'] || list.length;
          this.typeCountMap[typeValue] = typeSpecificCount;
        }
      });
      if (list.length < (pagination?.pageSize || 10)) {
        this.hasMoreExternal = false;
      }
      return list.map(ele => ({
        type: 'external',
        typeKey: this.getExternalTypeKey(ele.activity_type),
        typeLabel: this.getTypeLabel(this.getExternalTypeKey(ele.activity_type)),
        title: ele.title,
        date: ele.activity_date,
        platform: ele.platform,
        url: ele.url,
        keywordsArr: ele.labels ? ele.labels.split(',') : [],
      }));
    },
    // 内部数据转timeline格式
    async fetchInternalData(pagination) {
      if (!this.hasMoreInternal) return [];
      if (!this.internalFilterParams.engagement_type.length) return [];

      const result = await fetchPhysicianEngagements(this.physician_id, this.internalFilterParams, pagination);
      const dataList = result?.value || result || [];
      const totalCount = result?.['@odata.count'] || dataList.length;
      // 保存每个类型的总数计数
      this.internalFilterParams.engagement_type.forEach(engagementType => {
        const typeKey = this.getInternalTypeKey(engagementType);
        if (!this.typeCountMap[typeKey]) {
          this.typeCountMap[typeKey] = totalCount;
        }
      });
      if (dataList.length < (pagination?.pageSize || 10)) {
        this.hasMoreInternal = false;
      }
      return dataList.map(item => {
        let mrTitle = item.engagement_title;
        if (item.engagement_type === 'mr拜访' || item.engagement_type === 'msl拜访') {
          mrTitle = this.getPurposeLabel(item.engagement_purpose);
        }

        return {
          type: 'internal',
          typeKey: this.getInternalTypeKey(item.engagement_type),
          typeLabel: this.getEngagementTypeLabel(item.engagement_type),
          title: mrTitle,
          date: item.engagement_date,
          meeting: item.meeting,
          products: item.products,
          user_name: item.user_name,
        };
      });
    },
    // 医学洞见转timeline格式
    async fetchInsightData(pagination) {
      if (!this.hasMoreInsight) return [];
      const res = await fetchPhysicianMslInsights({ physician_id: this.physician_id }, (pagination?.page || 0) * (pagination?.pageSize || 10), pagination?.pageSize || 10);
      const list = res?.value || [];
      // 保存医学洞见的总数计数
      if (!this.typeCountMap.msl_insight) {
        this.typeCountMap.msl_insight = res?.['@odata.count'] || list.length;
      }
      if (list.length < (pagination?.pageSize || 10)) {
        this.hasMoreInsight = false;
      }
      return list.map(item => ({
        type: 'insight',
        typeKey: 'msl_insight',
        typeLabel: this.getTypeLabel('msl_insight'),
        title: item.insight_question?.question || this.$t('timeline_type_msl_insight'),
        date: item.insight_time,
        insight_content: item.insight_content,
        product: item.product,
        insight_user_name: item.insight_user_name,
      }));
    },
    // 学术数据转timeline格式
    async fetchAcademicData(pagination) {
      if (pagination && pagination.page > 0) return []; // 当前不分页请求

      const res = await Promise.all([
        py.getPublicationsByPhysicianId(this.physician_id),
        py.getPhysicianClinicalTrialsByPhysicianId(this.physician_id),
        py.getPhysicianProjectsByPhysicianId(this.physician_id),
        py.getPhysicianAwardsByPhysicianId(this.physician_id),
        py.getGuidesByPhysicianId(this.physician_id),
        py.getPatentsByPhysicianId(this.physician_id),
      ]).catch(() => {
        return [];
      });

      if (!res || !res.length) return [];

      let allAcademic = [];
      const types = [
        { key: 'physician__physician_publications', typeKey: 'publication', titleKey: 'publication_title', dateKey: 'publish_date' },
        { key: 'physician__physician_clinical_trials', typeKey: 'clinical_trial', titleKey: 'title', dateKey: 'registration_date' },
        { key: 'physician__physician_projects', typeKey: 'national_fund', titleKey: 'project_title', dateKey: 'exec_date_start' },
        { key: 'physician__physician_awards', typeKey: 'award', titleKey: 'title', dateKey: 'award_time' },
        { key: 'physician__physician_guides', typeKey: 'guideline', titleKey: 'topic', dateKey: 'release_time' },
        { key: 'physician__physician_patents', typeKey: 'patent', titleKey: 'patent_name', dateKey: 'publish_date' },
      ];

      res.forEach((result, idx) => {
        if (result && result.value && result.value.length) {
          const typeInfo = types[idx];
          // 保存该学术类型的总数计数（从 @odata.count 获取）
          if (!this.typeCountMap[typeInfo.typeKey]) {
            this.typeCountMap[typeInfo.typeKey] = result['@odata.count'] || result.value.length;
          }
          result.value.forEach(item => {
            let platformValue = item.journal || item.sponsor || item.award_name || '';
            if (typeInfo.typeKey === 'national_fund' && item.project_fund_name) {
              platformValue = item.project_fund_name;
            }
            let titleValue = item[typeInfo.titleKey] || this.getTypeLabel(typeInfo.typeKey);
            if (typeInfo.typeKey === 'publication' && typeof titleValue === 'string') {
              titleValue = titleValue.replace(/^《+|》+$/g, '');
            }
            allAcademic.push({
              type: 'academic',
              typeKey: typeInfo.typeKey,
              typeLabel: this.getTypeLabel(typeInfo.typeKey),
              title: titleValue,
              date: item[typeInfo.dateKey] || item.created_on,
              platform: platformValue, // 可以在平台显示额外信息
              url: item.url
            });
          });
        }
      });
      return allAcademic.filter(item => item.date);
    },
    // 按月/年分组
    partitionTimelineByMonth(list) {
      if (!list || !list.length) return [];
      const group = {};
      const currentYear = new Date().getFullYear();
      list.forEach(item => {
        let groupKey = 'unknown';
        if (item.date) {
          const itemYear = parseInt(item.date.substr(0, 4), 10);
          if (currentYear - itemYear >= 3) {
            groupKey = String(itemYear);
          } else {
            groupKey = `${item.date.substr(0, 4)}-${item.date.substr(5, 2)}`;
          }
        }
        if (!group[groupKey]) group[groupKey] = [];
        group[groupKey].push(item);
      });
      // 最新的分组排前面
      return Object.keys(group).sort((a, b) => b.localeCompare(a)).map(month => ({ month, items: group[month] }));
    },
    formatMonthLabel(monthStr) {
      if (!monthStr) return '';
      if (monthStr === 'unknown') {
        return this.$t('timeline_unknown_date');
      }
      if (/^\d{4}$/.test(monthStr)) {
        return monthStr;
      }
      if (/^\d{4}-\d{2}$/.test(monthStr)) {
        const [year, month] = monthStr.split('-');
        if (this.$i18n.locale === 'zh') {
          return `${year}<span style="font-size: 14px;">年</span>${parseInt(month, 10)}<span style="font-size: 14px;">月</span>`;
        }
        return new Intl.DateTimeFormat('en-US', {month: 'short', year: 'numeric'}).format(new Date(`${monthStr}-01`));
      }
      return monthStr;
    },
    // 渲染summary
    renderMonthSummary(items) {
      if (!items || !items.length) return '';
      const typeCount = {};
      items.forEach(i => { typeCount[i.typeKey] = (typeCount[i.typeKey] || 0) + 1; });
      return Object.entries(typeCount)
        .map(([k, v]) => `<span style="color: #666; font-size: 13px; font-weight: bold; margin-right: 2px;">${v}</span><span style="font-size: 12px;">${this.getTypeLabel(k)}</span>`)
        .join('<span style="margin: 0 4px; color: #999;"> </span>');
    },
    getInternalTypeKey(engagementType) {
      const map = {
        mr拜访: 'mr_call',
        msl拜访: 'msl_call',
        会议: 'internal_meeting',
      };
      return map[engagementType] || engagementType;
    },
    getExternalTypeKey(activityType) {
      const map = {
        公众号: 'wechat_article',
        患教文章: 'patient_article',
        患教直播: 'patient_live',
        第三方会议: 'third_party_meeting',
      };
      return map[activityType] || activityType;
    },
  },
};
</script>

<style scoped lang="scss">
.summary-item {
  width: 25%;
  margin-bottom: 10px;
  .label {
    font-size: 10px;
    color: #666;
    display: block;
    margin-bottom: 4px;
  }
  .value {
    padding: 10px;
    padding-top: 4px;
    display: block;
    font-size: 15px;

    color: #ab351c;
    .v-bd {
      font-weight: initial;
    }
    // &.publication {
    //     color: #ab351c;
    // }
    // &.clinical-trial {
    //     color: #aa341b;
    // }
    // &.project {
    //     color: #7EC0EE;
    // }
    // &.award {
    //     color: #FFD700;
    // }
  }
}

.mx-20px {
  margin: 0 20px;
}

.summary-padding {
  padding-top: 15px;
  padding-bottom: 15px;
}

::v-deep .van-tabs__nav {
  border-radius: 50px;
  overflow: hidden;
}

::v-deep .van-tabs__wrap {
  margin-bottom: 10px;
}

.flex-detail {
  display: flex;
  flex-wrap: wrap;
  justify-items: center;
  justify-content: space-between;
  span {
    padding-right: 10px;
  }
}
.c-s-item {
  font-size: 11px !important;
}
.new-icon {
  display: inline-block;
  padding: 0 5px;
  height: 18px;
  text-align: center;
  font-size: 9px;
  font-weight: 800;
  border-radius: 50px;
  background-color: #fff;
  color: rgb(255, 191, 0);
  border: 2px solid rgb(255, 191, 0);
}

::v-deep .van-step--vertical:not(:last-child)::after {
  border-bottom: none;
}

.timeline-summary-text {
  font-size: 12px;
  color: #666;
  margin-left: 4px;
  margin-top: 4px;
  line-height: 1.5;
  width: 100%;
}

.timeline-month-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  cursor: pointer;
  padding: 4px 0;
}

.timeline-card-group {
  background: #fff;
  border-radius: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
  overflow: hidden;
}

.timeline-card {
  padding: 10px 10px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  .card-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 6px;

    .type1 {
      padding: 1px 6px;
      border-radius: 12px;
      color: #fff;
      font-size: 10px;
    }
  }

  .card-title {
    font-size: 12px;
    font-weight: 400;
    color: #333;
    line-height: 1.4;
    margin-bottom: 4px;
  }

  .info-details {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
    margin-bottom: 6px;
  }

  .insight-content {
    margin-top: 6px;
    padding: 8px;
    background: #f8f8f8;
    border-radius: 6px;
    color: #666;
    font-size: 12px;
    line-height: 1.5;
    display: flex;
    flex-direction: column;

    .insight-text {
      display: inline-block;
      text-align: justify;
      position: relative;
      padding-left: 18px;

      .insight-icon {
        position: absolute;
        left: 0;
        top: 1px;
        font-size: 14px;
      }
    }

    .link-detail {
      color: #aa341b;
      font-size: 12px;
      cursor: pointer;
      text-align: right;
      margin-top: 4px;
      display: inline-flex;
      align-self: flex-end;
      align-items: center;
      justify-content: flex-end;
    }
  }
}

.legend-container {
  background: #f8f8f8;
  border-radius: 8px;
  padding: 12px 12px 6px 12px;
  margin: 5px 10px 15px;
  border: 1px solid #f4f4f4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .legend-group {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
    border-bottom: 1px dashed #f5f5f5;
    padding-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
      border-bottom: none;
      padding-bottom: 0;
    }

    .legend-group-title {
      width: 75px;
      flex-shrink: 0;
      font-size: 11px;
      color: #666;
      font-weight: 400;
      line-height: 18px;
    }

    .legend-items {
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      row-gap: 2px;
      padding-left: 4px;

      .legend-item {
        width: 33.33%;
        display: flex;
        align-items: center;
        min-height: 16px;
        margin-bottom: 6px;

        .legend-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 4px;
          flex-shrink: 0;
        }

        .legend-text {
          font-size: 10px;
          color: #555;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
}
</style>
